import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { ToastStore, type ToastInput } from './toastStore';

interface ToastApi { notify: (toast: ToastInput) => string; dismiss: (id: string) => void; }
const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children, store: suppliedStore }: { children: ReactNode; store?: ToastStore }) {
  const store = useMemo(() => suppliedStore ?? new ToastStore(), [suppliedStore]);
  const toasts = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const notify = useCallback((toast: ToastInput) => store.add(toast), [store]);
  const api = useMemo(() => ({ notify, dismiss: store.dismiss.bind(store) }), [notify, store]);
  return <ToastContext.Provider value={api}>{children}<div className="toast-region" aria-label="Oznámení">{toasts.map(toast => <ToastItem key={toast.id} toast={toast} dismiss={store.dismiss.bind(store)} />)}</div></ToastContext.Provider>;
}

function ToastItem({ toast, dismiss }: { toast: ReturnType<ToastStore['getSnapshot']>[number]; dismiss: (id: string) => void }) {
  useEffect(() => { const timer = window.setTimeout(() => dismiss(toast.id), toast.duration); return () => window.clearTimeout(timer); }, [dismiss, toast]);
  return <div className={`ui-toast ui-toast-${toast.severity}`} role={toast.severity === 'error' ? 'alert' : 'status'} aria-live={toast.severity === 'error' ? 'assertive' : 'polite'}><span>{toast.message}</span><button type="button" className="icon-button" aria-label="Zavřít oznámení" onClick={() => dismiss(toast.id)}><X aria-hidden="true" /></button></div>;
}

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used within ToastProvider');
  return value;
}
